import React, { useEffect, useState } from 'react';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import Overlay from '../Overlay';
import { itemLabel, itemUom } from '../../lib/requestItems';
import { closeCopyFor, closeReasonsFor, INVOICE } from '../../lib/shortClose';

// #288, Session 74z3. One sheet, opened from either flag, with its wording
// switched by which one called it.
//
// It is shared rather than duplicated because the two surfaces ask the same
// question in the same shape — a reason, then a confirm — and the thing that
// differs is copy, which closeCopyFor() owns. Duplicating it would put the
// required-reason rule in two places, and that rule is the whole point: the
// reason is the only record of why an order settled short, and it is what an
// executive asking why $4,180 was paid against a $5,200 order ends up reading.
//
// Rendered through Overlay's portal, never position: fixed (locked rule 3).
export default function ShortCloseSheet({
  visible, flag, item, req, isMobile,
  saving, error, done, result,
  onSubmit, onClose,
}) {
  const [reason, setReason] = useState('');

  // Reset when the sheet opens on a different line or a different flag. Keyed
  // on both because the same line can be closed for receiving and for billing,
  // and carrying the receipt reason into the billing sheet would pre-fill an
  // audit record with the wrong sentence.
  useEffect(() => {
    if (visible) setReason('');
  }, [visible, item?.id, flag]);

  if (!visible || !item) return null;

  const copy = closeCopyFor(flag);
  const chips = closeReasonsFor(flag);
  const accent = flag === INVOICE ? '#B31F7C' : '#0F0F0F';

  const trimmed = reason.trim();
  const canSubmit = trimmed.length > 0 && !saving;

  const received = item.qty_received != null ? parseFloat(item.qty_received) : 0;
  const requested = item.qty_requested != null ? parseFloat(item.qty_requested) : 0;
  const uom = itemUom(item);

  return (
    <Overlay variant={isMobile ? 'sheet' : 'modal'} maxWidth={520} onClose={saving ? undefined : onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
        {isMobile && <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D0D0D0', margin: '12px auto 0', flexShrink: 0 }} />}

        <div style={{ background: '#0F0F0F', padding: '14px 20px', flexShrink: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'white' }}>{copy.title}</div>
          <div style={{ fontSize: 11, color: '#9A9A9A', marginTop: 2 }}>
            {itemLabel(item)} · {received} of {requested} {uom} received
          </div>
        </div>

        {done ? (
          <div style={{ padding: '20px 20px 32px', overflowY: 'auto' }}>
            <div style={{ background: '#E8F5E9', borderRadius: 12, padding: 20, border: '1px solid #C8E6C9', textAlign: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>✓</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#2E7D32' }}>Line closed</div>
              {/* The function reports whether the order settled as a consequence.
                  Saying so here is the only place a person finds out that the
                  order they were working on is now finished. */}
              {result?.request_closed && (
                <div style={{ fontSize: 12, color: '#2E7D32', marginTop: 4 }}>
                  Every line is settled, so {req?.request_number || 'this order'} is now closed.
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: accent, color: 'white', cursor: 'pointer' }}
            >
              Done
            </button>
          </div>
        ) : (
          <div style={{ padding: '16px 20px 28px', overflowY: 'auto' }}>
            {error && (
              <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>
                {error}
              </div>
            )}

            {/* The note is here because a flag that sounds terminal is not one.
                SAP's delivery-completed indicator is non-blocking by design, and
                Stede matches it — so the receive path needs no new gate, and the
                screen has to say so or nobody will risk pressing the button. */}
            <div style={{
              display: 'flex', gap: 8, alignItems: 'flex-start', background: '#F7F7F6',
              border: '1px solid #E8E8E8', borderLeft: '3px solid #9A9A9A',
              borderRadius: '0 7px 7px 0', padding: '9px 12px', marginBottom: 16,
            }}>
              <IconInfoCircle size={14} color="#5C5C5C" style={{ flexShrink: 0, marginTop: 1 }} />
              <span style={{ fontSize: 11.5, color: '#5C5C5C', lineHeight: 1.5 }}>{copy.note}</span>
            </div>

            <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 7 }}>
              {copy.prompt} <span style={{ color: '#A32D2D' }}>*</span>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              {chips.map(c => {
                const on = trimmed === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setReason(on ? '' : c)}
                    style={{
                      fontSize: 11.5, fontWeight: 600, borderRadius: 20, padding: '5px 11px',
                      cursor: 'pointer',
                      background: on ? '#FFF8EC' : '#F5F5F3',
                      border: '1px solid ' + (on ? '#F3D9A8' : '#E4E4E0'),
                      color: on ? '#8A5A08' : '#3C3C3C',
                    }}
                  >
                    {c}
                  </button>
                );
              })}
            </div>

            {/* 16px minimum, locked rule 12 — anything smaller triggers iOS
                auto-zoom, and this sheet is a mobile-first surface. */}
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Pick one above, or type what happened"
              rows={3}
              style={{
                width: '100%', border: '1px solid #E0E0E0', borderRadius: 8,
                padding: '10px 12px', fontSize: 16, color: '#0F0F0F',
                fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
              }}
            />

            <div style={{ display: 'flex', gap: 9, marginTop: 16 }}>
              <button
                onClick={onClose}
                disabled={saving}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 10, fontSize: 13.5, fontWeight: 600,
                  background: 'white', color: '#0F0F0F', border: '1px solid #E0E0E0',
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => onSubmit(trimmed)}
                disabled={!canSubmit}
                style={{
                  flex: 2, padding: '12px 0', borderRadius: 10, fontSize: 13.5, fontWeight: 700,
                  border: 'none',
                  background: canSubmit ? accent : '#E0E0E0',
                  color: canSubmit ? 'white' : '#9A9A9A',
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                }}
              >
                {saving ? 'Saving…' : copy.confirm}
              </button>
            </div>

            {!trimmed && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 9, fontSize: 11, color: '#9A9A9A' }}>
                <IconAlertTriangle size={12} color="#BA7517" />
                A reason is required — it is the only record of why this order settled short.
              </div>
            )}
          </div>
        )}
      </div>
    </Overlay>
  );
}
